import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, downloadBlob } from "../../../api/fddClient";

/**
 * Fee Summary — turn a NetSuite "Time Billed by Project" export into weekly
 * hours and fees by person.
 *
 * Two screens:
 *   upload   — drop the export
 *   groups   — confirm which group each department rolls into, add groups of
 *              your own, order them, then build
 *
 * Every group becomes its own breakout with a subtotal on both output sheets,
 * so adding one here adds it to the workbook. Types mirror
 * server/fee-summary/types.ts.
 */

type DepartmentMapping = { department: string; group: string; rows: number; hours: number };

type UploadResponse = {
  sessionId: string;
  fileName: string;
  sheetName: string;
  rowCount: number;
  departments: DepartmentMapping[];
  groups: string[];
  people: number;
  weeks: string[];
  projectName: string;
  client: string;
  totals: { hours: number; original: number; realized: number };
  /** Things the totals cannot settle on their own. */
  warnings?: string[];
};

const GREEN = "var(--theme-ink)";
const UNMAPPED = "Unmapped";

const hint: React.CSSProperties = { fontSize: 11, color: "#6b7280" };
const selectStyle: React.CSSProperties = {
  fontSize: 11, padding: "2px 4px", borderRadius: 4, border: "1px solid var(--theme-border)", background: "#fff",
};
const inputStyle: React.CSSProperties = { ...selectStyle, padding: "3px 6px" };
const th: React.CSSProperties = {
  borderBottom: "1px solid var(--theme-border)", padding: "4px 6px", fontWeight: 700, color: GREEN,
  textAlign: "left", background: "var(--theme-tint-7)", fontSize: 11, whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  borderBottom: "1px solid #f1f5f9", padding: "4px 6px", color: "#374151", fontSize: 11,
  verticalAlign: "middle",
};
const btn = (primary: boolean): React.CSSProperties => ({
  fontSize: 12, fontWeight: 600, padding: "6px 14px", borderRadius: 6, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--theme-border)",
  background: primary ? GREEN : "#fff",
  color: primary ? "#fff" : GREEN,
});

/**
 * Read an error out of a response. A dev-proxy failure or a crashed server
 * answers with HTML, and calling res.json() on that throws a JSON parse error
 * that hides what actually went wrong.
 */
async function errorFrom(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const parsed = JSON.parse(text);
    if (parsed?.error) return String(parsed.error);
  } catch {
    // Not JSON — fall through to the raw text below.
  }
  const flat = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (/Error occurred while trying to proxy/i.test(flat)) {
    return "Can't reach the server. Restart the dev server (npm start) so it picks up this route.";
  }
  return flat.slice(0, 200) || `Request failed (${res.status}).`;
}

const num = (n: number, dp = 0) => n.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

type Props = { projectId: string; projectName?: string };

const FeeSummaryPanel: React.FC<Props> = ({ projectId, projectName }) => {
  const [info, setInfo] = useState<UploadResponse | null>(null);
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [busy, setBusy] = useState<"upload" | "build" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [newGroup, setNewGroup] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  // The session in a ref as well, for the unmount clean-up below.
  const sessionRef = useRef<string | null>(null);
  useEffect(() => { sessionRef.current = info?.sessionId ?? null; }, [info]);
  const discard = useCallback((sessionId: string | null) => {
    if (!sessionId) return;
    void apiFetch("/api/fee-summary/discard", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ projectId, sessionId }),
    }).catch(() => undefined);
  }, [projectId]);
  // Leaving the tool drops the upload with it, so the server's copy of the
  // engagement's time goes too rather than waiting out its idle hour.
  useEffect(() => () => discard(sessionRef.current), [discard]);

  const upload = useCallback(async (file: File) => {
    setBusy("upload");
    setError(null);
    try {
      const fd = new FormData();
      fd.append("projectId", projectId);
      fd.append("file", file);
      const res = await apiFetch("/api/fee-summary/upload", { method: "POST", body: fd });
      if (!res.ok) throw new Error(await errorFrom(res));
      const data = (await res.json()) as UploadResponse;
      setInfo(data);
      setAssignments(Object.fromEntries(data.departments.map((d) => [d.department, d.group])));
      setGroupOrder(data.groups.filter((g) => g !== UNMAPPED));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(null);
    }
  }, [projectId]);

  /** Groups with at least one department, which are the ones the workbook gets. */
  const usedGroups = useMemo(
    () => new Set(Object.values(assignments).filter((g) => g && g !== UNMAPPED)),
    [assignments],
  );

  // Every group the analyst has, in display order: the ones they declared,
  // then any assigned but not yet ordered. A group added before anything is
  // moved into it stays here, so it can be picked in the dropdowns.
  const groups = useMemo(() => {
    const ordered = groupOrder.filter((g, i) => groupOrder.indexOf(g) === i);
    for (const g of usedGroups) if (!ordered.includes(g)) ordered.push(g);
    return ordered;
  }, [usedGroups, groupOrder]);

  const unassigned = useMemo(
    () => Object.entries(assignments).filter(([, g]) => !g || g === UNMAPPED).map(([d]) => d),
    [assignments],
  );

  const addGroup = () => {
    const name = newGroup.trim();
    if (!name || name === UNMAPPED) return;
    // The group joins the list and every dropdown straight away; the analyst
    // chooses which departments go in it. It stays out of the workbook until
    // one does.
    setGroupOrder((prev) => (prev.includes(name) ? prev : [...groups, name]));
    setNewGroup("");
  };

  /** Drop a group nothing is assigned to. */
  const removeGroup = (group: string) => {
    if (usedGroups.has(group)) return;
    setGroupOrder((prev) => prev.filter((g) => g !== group));
  };

  const move = (group: string, delta: number) => {
    setGroupOrder(() => {
      const next = [...groups];
      const i = next.indexOf(group);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const build = async () => {
    if (!info) return;
    setBusy("build");
    setError(null);
    try {
      const res = await apiFetch("/api/fee-summary/build", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          sessionId: info.sessionId,
          mappings: Object.entries(assignments).map(([department, group]) => ({ department, group })),
          groupOrder: groups.filter((g) => usedGroups.has(g)),
          projectName: projectName || info.projectName,
        }),
      });
      if (!res.ok) throw new Error(await errorFrom(res));
      const blob = await res.blob();
      const disp = res.headers.get("Content-Disposition") || "";
      const match = /filename="([^"]+)"/.exec(disp);
      downloadBlob(blob, match?.[1] || "fee-summary.xlsx");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Build failed.");
    } finally {
      setBusy(null);
    }
  };

  const reset = () => {
    discard(info?.sessionId ?? null);
    setInfo(null);
    setAssignments({});
    setGroupOrder([]);
    setError(null);
  };

  return (
    <section style={{ border: "1px solid var(--theme-border)", borderRadius: 10, background: "var(--theme-tint-3)", padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontWeight: 700, color: GREEN, fontSize: 13 }}>Fee Summary</div>
        <div style={hint}>NetSuite time export &rarr; weekly hours and fees by person</div>
        {info && (
          <button type="button" style={{ ...btn(false), marginLeft: "auto" }} onClick={reset}>
            Start over
          </button>
        )}
      </div>

      {error && (
        <div style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", borderRadius: 6, padding: "6px 10px", fontSize: 11 }}>
          {error}
        </div>
      )}

      {!info ? (
        <div
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            // One upload at a time.
            if (busy) return;
            const f = e.dataTransfer?.files?.[0];
            if (f) void upload(f);
          }}
          onClick={() => { if (!busy) fileInput.current?.click(); }}
          style={{
            border: `1px dashed ${dragOver ? GREEN : "var(--theme-border)"}`, borderRadius: 8,
            background: dragOver ? "var(--theme-tint-11)" : "#fff", padding: 28, textAlign: "center",
            cursor: "pointer", color: "#6b7280", fontSize: 12,
          }}
        >
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              e.currentTarget.value = "";
              if (f) void upload(f);
            }}
          />
          {busy === "upload" ? "Reading the export…" : "Drop the NetSuite “Time Billed by Project” export here, or click to choose — .xlsx, .xls or .csv"}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 11, color: "#374151" }}>
            <span><strong>{info.fileName}</strong></span>
            <span>{num(info.rowCount)} rows</span>
            <span>{info.people} people</span>
            <span>{info.weeks.length} weeks ({info.weeks[0]} &ndash; {info.weeks[info.weeks.length - 1]})</span>
            <span>{num(info.totals.hours, 1)} hrs</span>
            <span>${num(info.totals.original)} original</span>
            <span>${num(info.totals.realized)} realized</span>
          </div>

          {(info.warnings ?? []).length > 0 && (
            <div style={{ border: "1px solid #fde68a", background: "#fffbeb", color: "#92400e", borderRadius: 6, padding: "6px 10px", fontSize: 11, display: "flex", flexDirection: "column", gap: 2 }}>
              {(info.warnings ?? []).map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}

          <div>
            <div style={{ fontWeight: 700, color: GREEN, fontSize: 12, marginBottom: 4 }}>Breakouts</div>
            <div style={{ ...hint, marginBottom: 6 }}>
              Each group below becomes its own block with a subtotal on both output sheets.
            </div>
            <table style={{ borderCollapse: "collapse", width: "100%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6 }}>
              <thead>
                <tr>
                  <th style={th}>Department</th>
                  <th style={{ ...th, textAlign: "right" }}>Rows</th>
                  <th style={{ ...th, textAlign: "right" }}>Hours</th>
                  <th style={th}>Group</th>
                </tr>
              </thead>
              <tbody>
                {info.departments.map((d) => {
                  const value = assignments[d.department] ?? UNMAPPED;
                  const options = Array.from(new Set([...groups, ...(value !== UNMAPPED ? [value] : [])]));
                  return (
                    <tr key={d.department}>
                      <td style={td}>{d.department}</td>
                      <td style={{ ...td, textAlign: "right" }}>{num(d.rows)}</td>
                      <td style={{ ...td, textAlign: "right" }}>{num(d.hours, 1)}</td>
                      <td style={td}>
                        <select
                          value={value}
                          onChange={(e) => setAssignments((prev) => ({ ...prev, [d.department]: e.target.value }))}
                          style={{ ...selectStyle, minWidth: 160, borderColor: value === UNMAPPED ? "#fca5a5" : "var(--theme-border)" }}
                        >
                          {value === UNMAPPED && <option value={UNMAPPED}>— pick a group —</option>}
                          {options.map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: GREEN }}>Order</span>
            {groups.map((g, i) => (
              <span key={g} style={{ display: "inline-flex", alignItems: "center", gap: 3, border: `1px ${usedGroups.has(g) ? "solid var(--theme-border)" : "dashed #d1d5db"}`, background: "#fff", borderRadius: 999, padding: "2px 4px 2px 10px", fontSize: 11, color: usedGroups.has(g) ? undefined : "#9ca3af" }}>
                {g}
                {!usedGroups.has(g) && (
                  <>
                    <span title="No department is in this group yet, so it is left out of the workbook"> (empty)</span>
                    <button type="button" title="Remove this group" onClick={() => removeGroup(g)} style={{ ...selectStyle, padding: "0 4px", cursor: "pointer" }}>&times;</button>
                  </>
                )}
                <button type="button" title="Move up" disabled={i === 0} onClick={() => move(g, -1)} style={{ ...selectStyle, padding: "0 4px", cursor: i === 0 ? "default" : "pointer", opacity: i === 0 ? 0.35 : 1 }}>&uarr;</button>
                <button type="button" title="Move down" disabled={i === groups.length - 1} onClick={() => move(g, 1)} style={{ ...selectStyle, padding: "0 4px", cursor: i === groups.length - 1 ? "default" : "pointer", opacity: i === groups.length - 1 ? 0.35 : 1 }}>&darr;</button>
              </span>
            ))}
            <input
              value={newGroup}
              onChange={(e) => setNewGroup(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGroup(); } }}
              placeholder="New group (e.g. Modelling)"
              style={{ ...inputStyle, width: 190 }}
            />
            <button type="button" style={btn(false)} onClick={addGroup} disabled={!newGroup.trim()}>Add group</button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button type="button" style={btn(true)} onClick={build} disabled={busy !== null || unassigned.length > 0}>
              {busy === "build" ? "Building…" : "Build fee summary"}
            </button>
            {unassigned.length > 0 && (
              <span style={{ ...hint, color: "#b91c1c" }}>
                Assign a group to {unassigned.join(", ")}.
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default FeeSummaryPanel;
